'use client';

import React, { useState, useEffect } from 'react';
import { MicrophoneIcon, CheckCircleIcon, PlayIcon } from '@heroicons/react/24/solid';
import { createClient } from '@/lib/supabase/client';

interface ScheduleItem {
  id: string;
  time_start: string;
  time_end: string;
  activity: string;
  tentative: boolean;
  sort_order: number;
}

function parseTime(t: string): number {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

function getStatus(time: string, endTime: string, nowMins: number): 'live' | 'done' | 'upcoming' {
  const start = parseTime(time);
  const end = parseTime(endTime);
  if (nowMins >= start && nowMins < end) return 'live';
  if (nowMins >= end) return 'done';
  return 'upcoming';
}

const emojiMap: Record<string, string> = {
  'Zumba': '💃',
  'Dinosaur Show': '🦕',
  'Milton Academy of Dance': '🩰',
  'RCA Cheerleading': '📣',
  'Falconry Demonstration': '🦅',
  'Rock Choir': '🎵',
};

export default function ArenaSchedule() {
  const [nowMins, setNowMins] = useState(0);
  const [mounted, setMounted] = useState(false);
  const [schedule, setSchedule] = useState<ScheduleItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setMounted(true);
    const supabase = createClient();

    // Clock tick for live status
    const update = () => {
      const now = new Date();
      setNowMins(now.getHours() * 60 + now.getMinutes());
    };
    update();
    const clockInterval = setInterval(update, 60000);

    // Initial fetch
    const fetchSchedule = async () => {
      try {
        const { data, error } = await supabase
          .from('schedule_items')
          .select('*')
          .order('sort_order', { ascending: true });
        if (!error && data) setSchedule(data);
      } catch {}
      finally { setLoading(false); }
    };
    fetchSchedule();

    // Real-time subscription for schedule_items
    const channel = supabase
      .channel('realtime:schedule_items')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'schedule_items' },
        async () => {
          // Re-fetch full sorted list on any change
          const { data, error } = await supabase
            .from('schedule_items')
            .select('*')
            .order('sort_order', { ascending: true });
          if (!error && data) setSchedule(data);
        }
      )
      .subscribe();

    return () => {
      clearInterval(clockInterval);
      supabase.removeChannel(channel);
    };
  }, []);

  return (
    <section>
      <div className="flex items-center gap-3 mb-4">
        <div className="gradient-primary rounded-xl p-2.5">
          <MicrophoneIcon className="w-5 h-5 text-secondary" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-foreground">Main Arena</h2>
          <p className="text-xs text-muted-foreground">Live entertainment all day</p>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-card rounded-2xl border border-border h-16 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="relative">
          <div className="absolute left-[27px] top-4 bottom-4 w-0.5 bg-border" />
          <div className="space-y-3">
            {schedule.map((event) => {
              const status = mounted ? getStatus(event.time_start, event.time_end, nowMins) : 'upcoming';
              const emoji = emojiMap[event.activity] || '🎪';

              return (
                <div key={event.id} className="flex gap-3 items-start relative">
                  <div className="relative z-10 mt-3 flex-shrink-0">
                    {status === 'live' ? (
                      <div className="w-6 h-6 rounded-full bg-red-500 flex items-center justify-center">
                        <PlayIcon className="w-2.5 h-2.5 text-white" />
                      </div>
                    ) : status === 'done' ? (
                      <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center">
                        <CheckCircleIcon className="w-3.5 h-3.5 text-muted-foreground" />
                      </div>
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-primary/20 border-2 border-primary flex items-center justify-center">
                        <div className="w-2 h-2 rounded-full bg-primary" />
                      </div>
                    )}
                  </div>

                  <div
                    className={`flex-1 rounded-2xl p-4 card-shadow border transition-all duration-200 ${
                      status === 'live' ? 'bg-red-50 border-red-200'
                        : status === 'done' ? 'bg-muted/50 border-border opacity-70' : 'bg-card border-border'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className="text-xl flex-shrink-0">{emoji}</span>
                        <div className="min-w-0">
                          <p className={`font-bold text-sm leading-tight ${status === 'done' ? 'text-muted-foreground' : 'text-foreground'}`}>
                            {event.activity}
                            {event.tentative && (
                              <span className="ml-1 text-xs font-normal text-muted-foreground">(TBC)</span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex-shrink-0 text-right">
                        <p className={`text-xs font-bold tabular-nums ${status === 'live' ? 'text-red-600' : 'text-muted-foreground'}`}>
                          {event.time_start}
                        </p>
                        <p className="text-[10px] text-muted-foreground/60 tabular-nums">– {event.time_end}</p>
                      </div>
                    </div>

                    {status === 'live' && (
                      <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-red-200">
                        <div className="live-dot" />
                        <span className="text-red-600 text-xs font-bold uppercase tracking-wider">Happening Now</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}